/**
 * Class representing a customizable modal component.
 * @category Components
 * @subcategory ui
 */
class ModalComponent {
  /**
   * Constructor for ModalComponent.
   * @param {Object} options - Configuration options for the modal.
   * @param {HTMLElement} [options.content] - The content to be displayed inside the modal.
   * @param {Function} [options.onClose] - Callback function to execute when the modal is closed.
   * @param {boolean} [options.draggable=true] - Flag to enable or disable drag functionality.
   * @param {boolean} [options.resizable=false] - Flag to enable or disable resize functionality.
   */
  constructor({ content, onClose, draggable = true, resizable = false }) {
    /**
     * @param {Function|null} - Callback function for the close event.
     */
    this.onClose = onClose;

    /**
     * @param {HTMLElement} - The modal container element.
     */
    this.modal = this.createModal();

    // Append provided content directly, no predefined sections
    if (content) this.modal.appendChild(content);

    // Enable drag and resize functionality based on flags
    if (draggable) this.setupDraggable();
    if (resizable) this.setupResizable();

    // Add modal to the document body
    document.body.appendChild(this.modal);
  }

  cleanup() {
    console.log(`✅ Cleaning up ModalComponent`);

    // Remove all event listeners for dragging
    this.modal.removeEventListener("mousedown", this.onMouseDown);
    document.removeEventListener("mousemove", this.onMouseMove);
    document.removeEventListener("mouseup", this.onMouseUp);

    // Remove event listeners for resizing if applicable
    if (this.resizer) {
        this.resizer.removeEventListener("mousedown", this.onResizeMouseDown);
        document.removeEventListener("mousemove", this.onResizeMouseMove);
        document.removeEventListener("mouseup", this.onResizeMouseUp);
    }

    // Remove modal from the DOM
    if (this.modal.parentNode) {
        this.modal.parentNode.removeChild(this.modal);
    }

    // Nullify references for garbage collection
    this.modal = null;
    this.onClose = null;
    this.resizer = null;
    this.onMouseDown = null;
    this.onMouseMove = null;
    this.onMouseUp = null;
    this.onResizeMouseDown = null;
    this.onResizeMouseMove = null;
    this.onResizeMouseUp = null;

    console.log(`✅ ModalComponent cleanup complete.`);
}

  /**
   * Creates the modal container element.
   * @returns {HTMLElement} - The modal container.
   */
  createModal() {
    const modal = document.createElement("div");
    modal.classList.add("modal-container");
    return modal;
  }

  /**
   * Enables drag functionality for the modal.
   */
  setupDraggable() {
    let offsetX, offsetY;

    const onMouseMove = (e) => {
      this.modal.style.left = `${e.clientX - offsetX}px`;
      this.modal.style.top = `${e.clientY - offsetY}px`;
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    const onMouseDown = (e) => {
      if (e.target.tagName !== "BUTTON" && e.target.tagName !== "INPUT") {
        offsetX = e.clientX - this.modal.offsetLeft;
        offsetY = e.clientY - this.modal.offsetTop;
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
      }
    };

    this.modal.addEventListener("mousedown", onMouseDown);
  }

  /**
   * Enables resize functionality for the modal.
   */
  setupResizable() {
    const resizer = document.createElement("div");
    resizer.classList.add("resizer");
    this.modal.appendChild(resizer);

    resizer.addEventListener("mousedown", (e) => {
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = this.modal.offsetWidth;
      const startHeight = this.modal.offsetHeight;

      const onMouseMove = (e) => {
        const newWidth = startWidth + (e.clientX - startX);
        const newHeight = startHeight + (e.clientY - startY);
        this.modal.style.width = `${newWidth}px`;
        this.modal.style.height = `${newHeight}px`;
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    });
  }

  /**
   * Disposes of the modal by removing it from the DOM.
   */
  dispose() {
    this.modal.remove();
  }

  /**
   * Triggers the close event, calls the onClose callback, and disposes of the modal.
   */
  triggerClose() {
    if (this.onClose) this.onClose();
    this.dispose();
  }
}

export default ModalComponent;
